import { maybe, Maybe } from '@passionware/monads';

/** URL-derived scope — workspaceId/teamId hold slugs from route params. */
export type AppScope =
  | { level: 'organization'; organizationId: string }
  | { level: 'workspace'; organizationId: string; workspaceId: string } // todo rename to slugs
  | {
      level: 'team';
      organizationId: string; // todo rename to slugs
      workspaceId: string; // todo rename to slugs
      teamId: string;
    };

/** Resolved scope — numeric ids for APIs plus slugs for URLs. */
export type ResolvedAppScope =
  | { level: 'organization'; organizationId: string }
  | {
      level: 'workspace';
      organizationId: string;
      workspaceId: number;
      workspaceSlug: string;
    }
  | {
      level: 'team';
      organizationId: string;
      workspaceId: number;
      workspaceSlug: string;
      teamId: number;
      teamSlug: string;
    };

/**
 * Id-only browsing scope for services / API filters (no slugs).
 * Derive from {@link ResolvedAppScope} via {@link appScopeUtils.identityFromResolved}.
 */
export type AppScopeIdentity =
  | { level: 'organization'; organizationId: string }
  | { level: 'workspace'; organizationId: string; workspaceId: number }
  | {
      level: 'team';
      organizationId: string;
      workspaceId: number;
      teamId: number;
    };

export type StoredAppScope = AppScope;

export const appScopeUtils = {
  identityFromResolved(scope: ResolvedAppScope): AppScopeIdentity {
    if (scope.level === 'organization') {
      return {
        level: 'organization',
        organizationId: scope.organizationId
      };
    }
    if (scope.level === 'workspace') {
      return {
        level: 'workspace',
        organizationId: scope.organizationId,
        workspaceId: scope.workspaceId
      };
    }
    return {
      level: 'team',
      organizationId: scope.organizationId,
      workspaceId: scope.workspaceId,
      teamId: scope.teamId
    };
  },

  /** Layer 3 — identity from org + stored entity workspace id (null → org-level). */
  identityFromEntityWorkspace(
    organizationId: string,
    enterpriseWorkspaceId: number | null | undefined
  ): AppScopeIdentity {
    if (enterpriseWorkspaceId != null) {
      return {
        level: 'workspace',
        organizationId,
        workspaceId: enterpriseWorkspaceId
      };
    }
    return { level: 'organization', organizationId };
  },

  /**
   * Scope for resource-type metastructure / fields UI.
   * Prefers the resource's stored workspace; create drafts without one use
   * browsing scope; existing legacy (null workspace) stay org-level.
   */
  identityForResourceMetastructure(
    browsing: ResolvedAppScope,
    resource: {
      id?: unknown;
      enterprise_workspace_id?: number | null;
    }
  ): AppScopeIdentity {
    if (resource.enterprise_workspace_id != null || resource.id) {
      return appScopeUtils.identityFromEntityWorkspace(
        browsing.organizationId,
        resource.enterprise_workspace_id
      );
    }
    return appScopeUtils.identityFromResolved(browsing);
  },

  getWorkspaceId(scope: AppScopeIdentity): Maybe<number> {
    if (scope.level === 'organization') {
      return maybe.ofAbsent();
    }
    return maybe.of(scope.workspaceId);
  },

  getWorkspaceSlug(scope: ResolvedAppScope): Maybe<string> {
    if (scope.level === 'organization') {
      return maybe.ofAbsent();
    }
    return scope.workspaceSlug;
  },

  getTeamSlug(scope: ResolvedAppScope): Maybe<string> {
    if (scope.level !== 'team') {
      return maybe.ofAbsent();
    }
    return scope.teamSlug;
  },

  assertValid(scope: AppScope): void {
    if (!scope.organizationId) {
      throw new Error('AppScope requires organizationId');
    }
    if (scope.level === 'workspace' && !scope.workspaceId) {
      throw new Error('workspace scope requires workspaceId');
    }
    if (scope.level === 'team') {
      if (!scope.workspaceId) {
        throw new Error('team scope requires workspaceId');
      }
      if (!scope.teamId) {
        throw new Error('team scope requires teamId');
      }
    }
  },

  isEnterpriseScopedPath(pathname: string): boolean {
    return /\/w\/[^/]+/.test(pathname);
  },

  equals(a: AppScope, b: AppScope): boolean {
    if (a.level !== b.level || a.organizationId !== b.organizationId) {
      return false;
    }
    if (a.level === 'organization') {
      return true;
    }
    if (a.level === 'workspace' && b.level === 'workspace') {
      return a.workspaceId === b.workspaceId;
    }
    if (a.level === 'team' && b.level === 'team') {
      return a.workspaceId === b.workspaceId && a.teamId === b.teamId;
    }
    return false;
  }
};
